import classNames from 'classnames';
import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  isAfter,
} from 'date-fns';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import type { IStyledComponent } from 'styled-components';
import styled from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import type { Props as TypographyProps } from '#src/atoms/typography/index.tsx';
import { HeadlineXL, LabelS } from '#src/atoms/typography/index.tsx';
import { useTickerContext } from '#src/contexts/ticker/index.tsx';

const CountdownWrapper = styled(Horizontal)`
  align-self: center;

  & > * {
    width: 2ch;
  }
`;
const Wrapper = styled(Vertical)`
  &.disabled {
    opacity: 0.5;
  }
`;

const StyledHeadlineXL = styled(HeadlineXL)`
  font: var(--t-48);
  font-variant-numeric: tabular-nums;
`;
const StyledLabelS = styled(LabelS).attrs({ color: 'var(--c-nd-600)' })``;

type Props = {
  date: Date;
  Label?: IStyledComponent<'web', TypographyProps>;
  Caption?: IStyledComponent<'web', TypographyProps>;
  greyOutZeros?: boolean;
  gap?: ComponentProps<typeof Horizontal>['gap'];
  separator?: ReactNode;
  onEnded?: () => void;
};

export const Countdown = ({
  date,
  Label = StyledHeadlineXL,
  Caption = StyledLabelS,
  greyOutZeros,
  gap = 3,
  separator,
  onEnded,
}: Props) => {
  const { now } = useTickerContext();
  const daysLeft = differenceInDays(date, now);
  const addedDays = addDays(now, daysLeft);
  const hoursLeft = differenceInHours(date, addedDays);
  const addedHours = addHours(addedDays, hoursLeft);
  const minutesLeft = differenceInMinutes(date, addedHours);
  const addedMinutes = addMinutes(addedHours, minutesLeft);
  const secondsLeft = differenceInSeconds(date, addedMinutes);
  const isEnded = isAfter(now, date);

  useEffect(() => {
    if (isEnded && onEnded) onEnded();
  }, [isEnded, onEnded]);

  if (isEnded) {
    return null;
  }
  return (
    <CountdownWrapper gap={gap}>
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({ disabled: greyOutZeros && daysLeft === 0 })}
        >
          <FormattedMessage
            defaultMessage="{count, plural, one {# <l>day</l>} other {# <l>days</l>}}"
            values={{
              count: daysLeft,
              l: chunks => <Caption as="span">{chunks}</Caption>,
            }}
          />
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({
            disabled: greyOutZeros && daysLeft === 0 && hoursLeft === 0,
          })}
        >
          <FormattedMessage
            defaultMessage="{count, plural, one {# <l>hour</l>} other {# <l>hours</l>}}"
            values={{
              count: hoursLeft,
              l: chunks => <Caption as="span">{chunks}</Caption>,
            }}
          />
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper
          gap={0}
          center
          className={classNames({
            disabled:
              greyOutZeros &&
              daysLeft === 0 &&
              hoursLeft === 0 &&
              minutesLeft === 0,
          })}
        >
          <FormattedMessage
            defaultMessage="{count, plural, one {# <l>minutes</l>} other {# <l>minutes</l>}}"
            values={{
              count: minutesLeft,
              l: chunks => <Caption as="span">{chunks}</Caption>,
            }}
          />
        </Wrapper>
      </Label>
      {separator}
      <Label as="div">
        <Wrapper gap={0} center>
          <FormattedMessage
            defaultMessage="{count, plural, one {# <l>second</l>} other {# <l>seconds</l>}}"
            values={{
              count: secondsLeft,
              l: chunks => <Caption as="span">{chunks}</Caption>,
            }}
          />
        </Wrapper>
      </Label>
    </CountdownWrapper>
  );
};
